import { useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useCreateContract } from '../../hooks/useApi';
import { contractDownloadUrl, errorMessage } from '../../services/api';
import { toast } from '../../store';
import { formatCurrency, toDateInput } from '../../utils/helpers';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { CarSelect, DriverSelect } from './EntitySelects';
import { t } from '../../i18n';

interface Values {
  driver_id: string;
  car_id: string;
  date_from: string;
  date_to: string;
  daily_rate: string;
}

/** Handover of a reservation: the contract is pre-filled and linked to it. */
export interface ContractPreset {
  reservation_id: number;
  driver_id: number;
  car_id: number;
  date_from: string;
  date_to: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  driverId?: number;
  preset?: ContractPreset | null;
}

export function ContractCreateModal({ isOpen, onClose, driverId, preset }: Props) {
  const create = useCreateContract();
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<Values>();

  useLayoutEffect(() => {
    if (!isOpen) return;
    reset({
      driver_id: String(preset?.driver_id ?? driverId ?? ''),
      car_id: preset ? String(preset.car_id) : '',
      date_from: preset?.date_from ?? toDateInput(),
      date_to: preset?.date_to ?? toDateInput(),
      daily_rate: '',
    });
  }, [isOpen, driverId, preset, reset]);

  const [dateFrom, dateTo, rate] = watch(['date_from', 'date_to', 'daily_rate']);
  const days = dateFrom && dateTo && dateTo >= dateFrom ? Math.round((Date.parse(dateTo) - Date.parse(dateFrom)) / 86_400_000) + 1 : 0;
  const total = rate !== '' && Number(rate) >= 0 && days ? Number(rate) * days : null;

  const onSubmit = async (values: Values) => {
    try {
      const contract = await create.mutateAsync({
        // Disabled (pre-selected) fields are not included in the submitted values
        driver_id: driverId ?? Number(values.driver_id),
        car_id: Number(values.car_id),
        date_from: values.date_from,
        date_to: values.date_to,
        daily_rate: values.daily_rate === '' ? null : Number(values.daily_rate),
        reservation_id: preset?.reservation_id ?? null,
      });
      // The response is an attachment, so the browser downloads it and stays on the page
      window.location.assign(contractDownloadUrl(contract.id));
      toast.success(t('Zmluva {number} bola vygenerovaná', { number: contract.number }));
      onClose();
    } catch (error) {
      toast.error(errorMessage(error, t('Zmluvu sa nepodarilo vygenerovať')));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      busy={create.isPending}
      size="lg"
      title={t('Generovať zmluvu')}
      description={t('Zmluva o nájme sa vyplní údajmi pracovníka a vozidla a stiahne sa ako dokument Word. Vozidlo sa zároveň pridelí pracovníkovi (Pridelenie).')}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={create.isPending}>
            {t('Zrušiť')}
          </Button>
          <Button type="submit" form="contract-form" loading={create.isPending}>
            {t('Generovať')}
          </Button>
        </>
      }
    >
      <form id="contract-form" onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 gap-4 sm:grid-cols-2" noValidate>
        <DriverSelect
          label={t('Pracovník')}
          required
          disabled={!!driverId}
          error={errors.driver_id?.message}
          {...register('driver_id', { required: driverId ? false : t('Vyberte pracovníka') })}
        />
        <CarSelect label={t('Vozidlo')} required onlyActive error={errors.car_id?.message} {...register('car_id', { required: t('Vyberte vozidlo') })} />
        <Input label={t('Od')} type="date" required error={errors.date_from?.message} {...register('date_from', { required: t('Zadajte dátum') })} />
        <Input
          label={t('Do')}
          type="date"
          required
          error={errors.date_to?.message}
          {...register('date_to', {
            required: t('Zadajte dátum'),
            validate: (value, all) => !all.date_from || value >= all.date_from || t('Dátum „do“ nesmie byť skôr ako „od“'),
          })}
        />
        <Input
          label={t('Cena za deň (€ bez DPH)')}
          type="number"
          inputMode="decimal"
          min={0}
          step="0.01"
          placeholder={t('nevyplnené – doplní sa ručne')}
          error={errors.daily_rate?.message}
          hint={total !== null ? t(days === 1 ? 'Spolu za {days} deň: {total} bez DPH' : days < 5 ? 'Spolu za {days} dni: {total} bez DPH' : 'Spolu za {days} dní: {total} bez DPH', { days, total: formatCurrency(total) }) : undefined}
          {...register('daily_rate', { validate: (v) => v === '' || Number(v) >= 0 || t('Cena nesmie byť záporná') })}
        />
      </form>
    </Modal>
  );
}
